import { useCallback } from "react";
import { useDebounce } from "use-debounce";
import {
  type RenderContentEmailWithContentBlocksV2Query,
  type RenderEmailHtmlWithContentBlocksQuery,
  useContentEmailPersonalizationPreviewQuery,
  useRenderContentEmailWithContentBlocksV2Query,
  useRenderEmailHtmlWithContentBlocksQuery,
} from "src/graphql";

const LOCAL_PREVIEW_DEBOUNCE_MS = 400;

type RenderQueryData =
  | RenderContentEmailWithContentBlocksV2Query
  | RenderEmailHtmlWithContentBlocksQuery;

/** Each render mode's query returns the html under its own resolver name. */
function htmlFromRenderQueryData(
  data: RenderQueryData | undefined,
): string | undefined {
  if (!data) {
    return undefined;
  }
  if ("renderContentEmailWithContentBlocksV2" in data) {
    return data.renderContentEmailWithContentBlocksV2.html ?? undefined;
  }
  return data.renderEmailHtmlWithContentBlocks.html ?? undefined;
}

export type LocalEmailTemplate = {
  html: string;
  subject?: string | null;
  preheader?: string | null;
};

type PreviewMode = "v2-saved" | "v2-local" | "local-only";

type Props = {
  contentEmailId: string;
  versionId?: string | null;
  campaignId?: string;
  localSource?: LocalEmailTemplate;
  enabled?: boolean;
  personalizationEnabled?: boolean;
  sampleCount?: number;
  currentValues?: unknown;
};

export function useEmailPreviewRender({
  contentEmailId,
  versionId,
  campaignId,
  localSource,
  enabled = true,
  personalizationEnabled = false,
  sampleCount = 1,
  currentValues,
}: Props) {
  const [debouncedHtml] = useDebounce(
    localSource?.html,
    LOCAL_PREVIEW_DEBOUNCE_MS,
  );
  const [debouncedSubject] = useDebounce(
    localSource?.subject,
    LOCAL_PREVIEW_DEBOUNCE_MS,
  );
  const [debouncedPreheader] = useDebounce(
    localSource?.preheader,
    LOCAL_PREVIEW_DEBOUNCE_MS,
  );
  const debouncedLocalSource =
    localSource && debouncedHtml !== undefined
      ? {
          html: debouncedHtml,
          subject: debouncedSubject,
          preheader: debouncedPreheader,
        }
      : undefined;

  // Before the debounce settles there is no server render that matches what
  // the caller is showing, and rendering the saved version instead would put
  // pre-edit content on screen.
  const mode: PreviewMode = localSource
    ? debouncedLocalSource
      ? "v2-local"
      : "local-only"
    : "v2-saved";
  const isLocal = mode === "v2-local";

  const personalizationQueryEnabled =
    enabled &&
    personalizationEnabled &&
    Boolean(contentEmailId) &&
    (isLocal || Boolean(versionId));

  // The personalization resolver expands content blocks server-side and returns
  // the rendered email, so running a render query alongside it would double both
  // the expansion work and the provider traffic for one preview.
  const renderEnabled = enabled && !personalizationQueryEnabled;

  const savedRender = useRenderContentEmailWithContentBlocksV2Query(
    { contentEmailId, versionId, campaignId },
    { enabled: renderEnabled && mode === "v2-saved" },
  );
  const localRender = useRenderEmailHtmlWithContentBlocksQuery(
    {
      contentEmailId,
      campaignId,
      localSource: debouncedLocalSource ?? { html: "" },
    },
    { enabled: renderEnabled && isLocal },
  );

  const personalization = useContentEmailPersonalizationPreviewQuery(
    {
      input: {
        email: {
          contentEmailId,
          versionId: isLocal ? undefined : versionId,
        },
        campaignId,
        localSource: isLocal ? debouncedLocalSource : undefined,
        sampleCount,
        currentValues,
      },
    },
    {
      enabled: personalizationQueryEnabled,
      keepPreviousData: true,
      retry: false,
    },
  );

  const activeRender =
    mode === "v2-local"
      ? localRender
      : mode === "v2-saved"
        ? savedRender
        : undefined;
  const v2Render = isLocal
    ? localRender.data?.renderEmailHtmlWithContentBlocks
    : savedRender.data?.renderContentEmailWithContentBlocksV2;
  const personalizationResult =
    personalization.data?.getContentEmailPersonalizationPreview;

  // Only fold personalization into loading/error when its query is actually
  // running — otherwise a disabled query's state would leak into the result.
  const personalizationLoading =
    personalizationQueryEnabled && personalization.isLoading;
  const personalizationError = personalizationQueryEnabled
    ? personalization.error
    : undefined;

  // In local-only mode every server render describes the saved version, so the
  // caller falls back to the content it already has rather than showing content
  // from before its own edits.
  const rendered =
    mode === "local-only"
      ? undefined
      : personalizationQueryEnabled
        ? (personalizationResult?.renderedEmail ?? undefined)
        : v2Render;

  // Exports (PDF) need the same content-block-expanded HTML the preview shows,
  // but they run from a click — the render query may never have been enabled
  // (Edit tab) and would otherwise print raw `{% content_block %}` / snippet
  // tags where the preview resolved the block's content. `refetch()` fetches
  // even while a query is disabled, so this reuses the mode and variables the
  // hook already resolved rather than rebuilding them per mode. Resolves
  // undefined when no server render describes what's on screen (local-only
  // mode) or the fetch fails, leaving the caller with its own HTML.
  const fetchRenderedHtml = useCallback(async (): Promise<
    string | undefined
  > => {
    if (rendered?.html) {
      return rendered.html;
    }
    if (mode === "local-only" || !contentEmailId) {
      return undefined;
    }
    try {
      const { data } =
        mode === "v2-saved"
          ? await savedRender.refetch()
          : await localRender.refetch();
      return htmlFromRenderQueryData(data);
    } catch {
      // Fail open: an export of the unexpanded HTML beats no export. The
      // preview surfaces render errors of its own, so this stays quiet.
      return undefined;
    }
  }, [contentEmailId, localRender, mode, rendered?.html, savedRender]);

  return {
    rendered,
    fetchRenderedHtml,
    personalization: personalizationResult,
    unresolvedContentBlocks: rendered?.unresolvedContentBlocks ?? [],
    verificationErrors: rendered?.verificationErrors ?? [],
    // Only the personalization resolver lints, so this is empty in the plain
    // render modes rather than stale from a previous personalization preview.
    templateDialectWarnings: personalizationQueryEnabled
      ? (personalizationResult?.templateDialectWarnings ?? [])
      : [],
    isLoading: Boolean(activeRender?.isLoading) || personalizationLoading,
    isFetchingPersonalization: personalization.isFetching,
    isPreviousPersonalizationData: personalization.isPreviousData,
    // A disabled react-query v3 query keeps its cached error, so gate the
    // render error the same way personalizationError is gated above — a stale
    // render failure must not blank the personalization preview.
    error:
      (renderEnabled ? activeRender?.error : undefined) ?? personalizationError,
  };
}
